import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  BackgroundVariant,
  Node,
  Edge,
  ConnectionMode,
  MarkerType
} from '@xyflow/react';
import { Pencil, PlusCircle, Trash2 } from 'lucide-react';
import { useAppSelector } from 'hooks';
import {
  useGetConfigurationLabels,
  useGetWorkflowDetail,
  useListConfigurations
} from 'api/sdk';
import { RootState } from 'store';
import Styles from './ActivityFlow.module.scss';
import { setObjectMapping } from 'store/annotations/annotationSlice';
import '@xyflow/react/dist/style.css';
import { Button } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import * as dagre from 'dagre';
import { useNavigate } from 'react-router-dom';
import SearchSelect from 'components/SearchSelect';
import clsx from 'clsx';

// Type definitions
interface Label {
  id: string;
  label_name: string;
}

interface NodeData {
  label: string;
  labelId: string | null;
  availableLabelsCount: number;
  onEdit: (nodeId: string) => void;
  onAddChild: (parentId: string) => void;
  onDelete?: (nodeId: string, currentEdges?: Edge[]) => void;
  isLabelSelectionActive: boolean;
  currentEdges?: Edge[];
}

interface CustomNode extends Node<any> {
  id: string;
  type: 'custom';
  position: { x: number; y: number };
}

interface LabelSelectorState {
  nodeId: string;
  position: { x: number; y: number };
}

interface ObjectDependency {
  id: string;
  dependency: string | null;
  label: string;
}

interface EditLabelDialogProps {
  nodeId: string | null;
  show: boolean;
}

interface CustomNodeProps {
  data: NodeData;
  id: string;
}

const CustomNode = ({ data, id }: CustomNodeProps) => {
  // Add this at the start of the component
  const areLabelsAvailable = data.availableLabelsCount > 0 && data.label;
  const isAddChildDisabled = !areLabelsAvailable || data.isLabelSelectionActive;

  return (
    <div className={Styles.nodeContainer}>
      <Handle
        type="target"
        position={Position.Top}
        className={Styles.nodeHandle}
        isConnectable={false}
      />

      <div className={Styles.nodeHeader}>
        <span className={Styles.nodeLabel}>
          {data.label || 'Select a label'}
        </span>
        <div className={Styles.nodeActions}>
          {data.label && data.availableLabelsCount > 0 && (
            <button
              className={Styles.nodeEditButton}
              onClick={e => {
                e.stopPropagation();
                data.onEdit(id);
              }}
            >
              <Pencil size={16} />
            </button>
          )}
          {id !== 'node-1' && (
            <button
              className={Styles.nodeDeleteButton}
              onClick={e => {
                e.stopPropagation();
                if (data.onDelete) {
                  data.onDelete(id, data.currentEdges);
                }
              }}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      <div className={Styles.addChildContainer}>
        <button
          className={`${Styles.addChildButton} ${
            isAddChildDisabled ? Styles.disabled : ''
          }`}
          onClick={e => {
            e.stopPropagation();
            if (!isAddChildDisabled) {
              data.onAddChild(id);
            }
          }}
          disabled={isAddChildDisabled}
          title={
            !areLabelsAvailable
              ? 'No more labels available'
              : data.isLabelSelectionActive
              ? 'Please complete label selection first'
              : 'Add child node'
          }
        >
          <PlusCircle size={20} />
          Add Child{' '}
          {areLabelsAvailable
            ? `(${data.availableLabelsCount} left)`
            : `(${data.availableLabelsCount} left)`}
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className={Styles.nodeHandle}
        isConnectable={false}
      />
    </div>
  );
};

const ActivityFlow: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const { data: configs, isLoading: loadingConfigs } = useListConfigurations();
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  const [canSaveAndProceed, setCanSaveAndProceed] = useState(false);
  const [usedLabels, setUsedLabels] = useState<Set<string>>(new Set());
  const edgesRef = useRef<Edge[]>([]);
  const [activeLabelSelector, setActiveLabelSelector] =
    useState<LabelSelectorState | null>(null);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const objectMapping: any = useAppSelector(
    (state: RootState) => state.annotation.objectMapping
  );
  const navigate = useNavigate();
  const [objectDependency, setObjectDependency] = useState<ObjectDependency[]>(
    []
  );
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const dispatch = useDispatch();
  const currentConfigId: any = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const { data: getExistingLabels } =
    useGetConfigurationLabels<any>(currentConfigId);
  const { data: workflowDetails }: any = useGetWorkflowDetail(currentConfigId);
  const totalLabelsCount = getExistingLabels?.data?.labels?.length || 0;

  const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setGraph({ rankdir: 'TB', nodesep: 80, ranksep: 100 });
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Add nodes to dagre
    nodes.forEach(node => {
      dagreGraph.setNode(node.id, { width: 250, height: 100 });
    });

    // Add edges to dagre
    edges.forEach(edge => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Layout the graph
    dagre.layout(dagreGraph);

    // Get the positioned nodes from dagre
    const layoutedNodes = nodes.map(node => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 125, // center the node (width/2)
          y: nodeWithPosition.y - 50 // center the node (height/2)
        }
      };
    });

    return { nodes: layoutedNodes, edges };
  };

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  useEffect(() => {
    const totalLabels = getExistingLabels?.data?.labels?.length || 0;
    const assignedLabels = usedLabels.size;
    setCanSaveAndProceed(assignedLabels >= Math.ceil(totalLabels / 2));
  }, [usedLabels.size, getExistingLabels?.data?.labels?.length]);

  useEffect(() => {
    if (workflowDetails?.data?.objectMapping?.length) {
      const newNodes: CustomNode[] = workflowDetails.data.objectMapping.map(
        (item: any, index: number) => ({
          id: `node-${index + 1}`,
          type: 'custom',
          data: {
            label: item.label,
            labelId: item.id,
            onEdit: (nodeId: string) => setEditingNodeId(nodeId),
            onAddChild: (parentId: string) => handleAddChild(parentId),
            onDelete: (nodeId: string) => handleDeleteNode(nodeId, edges),
            availableLabelsCount: getExistingLabels?.data?.labels?.length || 0
          },
          position: { x: 0, y: 0 }
        })
      );

      const newEdges = workflowDetails.data.objectMapping
        .filter((item: any) => item.dependency)
        .map((item: any) => {
          const sourceNode = newNodes.find(
            node => node.data.labelId === item.dependency
          );
          const targetNode = newNodes.find(
            node => node.data.labelId === item.id
          );

          if (sourceNode && targetNode) {
            return {
              id: `e${sourceNode.id}-${targetNode.id}`,
              source: sourceNode.id,
              target: targetNode.id,
              type: 'step',
              style: { strokeWidth: 2, stroke: '#4299e1' },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 15,
                height: 15,
                color: '#4299e1'
              }
            };
          }
          return null;
        })
        .filter(Boolean);

      // Layout with dagre
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(newNodes, newEdges as Edge[]);

      setNodes(layoutedNodes);
      setEdges(layoutedEdges as any[]);
      setObjectDependency(workflowDetails?.data?.objectMapping);

      const usedLabelSet: any = new Set(
        workflowDetails.data.objectMapping.map((item: any) => item.label)
      );
      setUsedLabels(usedLabelSet);
    } else if (
      !workflowDetails?.data?.objectMapping?.length &&
      objectMapping?.length
    ) {
      const newNodes: CustomNode[] = objectMapping.map(
        (item: any, index: number) => ({
          id: `node-${index + 1}`,
          type: 'custom',
          data: {
            label: item.label,
            labelId: item.id,
            onEdit: (nodeId: string) => setEditingNodeId(nodeId),
            onAddChild: (parentId: string) => handleAddChild(parentId),
            onDelete: (nodeId: string) => handleDeleteNode(nodeId, edges),
            availableLabelsCount: getExistingLabels?.data?.labels?.length || 0
          },
          position: { x: 0, y: 0 }
        })
      );

      const newEdges = objectMapping
        .filter((item: any) => item.dependency)
        .map((item: any) => {
          const sourceNode = newNodes.find(
            node => node.data.labelId === item.dependency
          );
          const targetNode = newNodes.find(
            node => node.data.labelId === item.id
          );

          if (sourceNode && targetNode) {
            return {
              id: `e${sourceNode.id}-${targetNode.id}`,
              source: sourceNode.id,
              target: targetNode.id,
              type: 'step',
              style: { strokeWidth: 2, stroke: '#4299e1' },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 15,
                height: 15,
                color: '#4299e1'
              }
            };
          }
          return null;
        })
        .filter(Boolean);

      // Layout with dagre
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(newNodes, newEdges as Edge[]);

      setNodes(layoutedNodes);
      setEdges(layoutedEdges as any[]);
      setObjectDependency(objectMapping);

      const usedLabelSet: any = new Set(
        objectMapping.map((item: any) => item.label)
      );
      setUsedLabels(usedLabelSet);
    } else if (nodes.length === 0) {
      const initialNode = {
        id: 'node-1',
        type: 'custom',
        data: {
          label: '',
          labelId: null,
          onEdit: (nodeId: string) => setEditingNodeId(nodeId),
          onAddChild: (parentId: string) => handleAddChild(parentId),
          onDelete: (nodeId: string) => handleDeleteNode(nodeId, edges),
          availableLabelsCount: getExistingLabels?.data?.labels?.length || 0,
          isLabelSelectionActive: true
        },
        position: { x: window.innerWidth / 2 - 100, y: 50 }
      };

      setNodes([initialNode]);
      setActiveLabelSelector({
        nodeId: 'node-1',
        position: { x: window.innerWidth / 2 + 120, y: 100 }
      });
    }
  }, [workflowDetails?.data, getExistingLabels?.data?.labels?.length]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Escape') {
        // Force mouseup event to release any drag operations
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(mouseUpEvent);

        // For touch devices
        const touchEndEvent = new TouchEvent('touchend', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(touchEndEvent);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Function to calculate available labels count
  const getAvailableLabelsCount = useCallback(() => {
    return totalLabelsCount - usedLabels.size;
  }, [totalLabelsCount, usedLabels.size]);

  const updateNodesWithAvailableLabels = useCallback(
    (currentNodes: any[]) => {
      const availableCount = getAvailableLabelsCount();
      return currentNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          availableLabelsCount: availableCount
        }
      }));
    },
    [getAvailableLabelsCount]
  );

  // Effect to update available labels count for all nodes
  useEffect(() => {
    setNodes(prev => updateNodesWithAvailableLabels(prev));
  }, [usedLabels.size, totalLabelsCount, updateNodesWithAvailableLabels]);

  const edgeOptions = {
    type: 'step', // Changed from smoothstep to step
    animated: true,
    style: {
      strokeWidth: 2,
      stroke: '#4299e1'
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 15,
      height: 15,
      color: '#4299e1'
    }
  };

  useEffect(() => {
    const currentlyUsedLabels = new Set(
      nodes.map(node => node.data.label).filter(Boolean)
    );
    setUsedLabels(currentlyUsedLabels);
  }, [nodes]);

  const calculateNodePosition = useCallback(
    (parentId: string): { x: number; y: number } => {
      const parentNode = nodes.find(node => node.id === parentId);
      if (!parentNode) return { x: 0, y: 0 };

      const VERTICAL_SPACING = 150;
      const MAX_HORIZONTAL_OFFSET = 200;

      // Generate random offset
      const randomOffset =
        Math.floor(Math.random() * MAX_HORIZONTAL_OFFSET) -
        MAX_HORIZONTAL_OFFSET / 2;

      return {
        x: parentNode.position.x + randomOffset,
        y: parentNode.position.y + VERTICAL_SPACING
      };
    },
    [nodes]
  );

  const updateObjectDependency = useCallback(() => {
    const newDependency: ObjectDependency[] = nodes
      .filter(node => node.data.label && node.data.labelId)
      .map(node => {
        const parentEdge = edges.find(edge => edge.target === node.id);
        const parentNode = parentEdge
          ? nodes.find(n => n.id === parentEdge.source)
          : null;

        return {
          id: node.data.labelId!,
          dependency: parentNode?.data.labelId || null,
          label: node.data.label
        };
      });

    setObjectDependency(newDependency);
  }, [nodes, edges]);

  useEffect(() => {
    updateObjectDependency();
  }, [nodes, edges, updateObjectDependency]);

  const handleDeleteNode = (nodeId: string, edgesProvided: Edge[]) => {
    // Get current edges directly from state to ensure latest reference

    const edgesToUse = edgesRef.current;

    // Helper function to find all descendants efficiently using current edges
    const findAllDescendants = (
      startNodeId: string,
      edgeList: Edge[]
    ): string[] => {
      const descendants = new Set<string>();

      function traverse(currentId: string) {
        // Find all child edges that start from this node
        const childEdges = edgeList.filter(
          (edge: any) => edge.source === currentId
        );

        // For each child edge, add the target and traverse deeper
        childEdges.forEach((edge: any) => {
          const childId = edge.target;
          if (!descendants.has(childId)) {
            descendants.add(childId);
            traverse(childId);
          }
        });
      }

      // Start traversal from the given node
      traverse(startNodeId);
      return Array.from(descendants);
    };

    // Get all nodes to delete (the node itself and all its descendants)
    const descendantIds = findAllDescendants(nodeId, edgesToUse);
    const nodeIdsToDelete = [nodeId, ...descendantIds];

    // Remove the labels from usedLabels set
    const nodesToDelete = nodes.filter(node =>
      nodeIdsToDelete.includes(node.id)
    );
    setUsedLabels(prev => {
      const newSet = new Set(prev);
      nodesToDelete.forEach(node => {
        if (node.data.label) {
          newSet.delete(node.data.label);
        }
      });
      return newSet;
    });

    // Delete nodes and their edges
    setNodes(prevNodes => {
      const remainingNodes = prevNodes.filter(
        node => !nodeIdsToDelete.includes(node.id)
      );
      return remainingNodes;
    });

    setEdges(prevEdges => {
      const remainingEdges = prevEdges.filter(
        edge =>
          !nodeIdsToDelete.includes(edge.source) &&
          !nodeIdsToDelete.includes(edge.target)
      );
      return remainingEdges;
    });

    // Force update the object dependency
    setTimeout(() => {
      updateObjectDependency();
    }, 0);
  };

  const handleAddChild = useCallback(
    (parentId: string) => {
      const availableLabels = getAvailableLabelsCount();

      if (getExistingLabels?.data?.labels?.length - usedLabels.size <= 0) {
        return;
      }

      const newNodeId = `node-${Date.now()}`;
      setNodes(prevNodes => {
        // Update all existing nodes to set isLabelSelectionActive to true
        const updatedNodes = prevNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            isLabelSelectionActive: true
          }
        }));

        const newNode = {
          id: newNodeId,
          type: 'custom',
          data: {
            label: '',
            labelId: null,
            onEdit: (nodeId: string) => setEditingNodeId(nodeId),
            onAddChild: (parentId: string) => handleAddChild(parentId),
            onDelete: (nodeId: string) => handleDeleteNode(nodeId, edges),
            availableLabelsCount: availableLabels - 1,
            isLabelSelectionActive: true
          },
          position: { x: 0, y: 0 }
        };

        const newEdge = {
          id: `e${parentId}-${newNodeId}`,
          source: parentId,
          target: newNodeId,
          type: 'step',
          style: { strokeWidth: 2, stroke: '#4299e1' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 15,
            height: 15,
            color: '#4299e1'
          }
        };

        // Create new dagre graph
        const dagreGraph = new dagre.graphlib.Graph();
        dagreGraph.setGraph({
          rankdir: 'TB',
          nodesep: 80,
          ranksep: 100,
          align: 'DL' // Add this for better alignment
        });
        dagreGraph.setDefaultEdgeLabel(() => ({}));

        // Add all nodes including the new one
        const allNodes = [...updatedNodes, newNode];
        allNodes.forEach(node => {
          dagreGraph.setNode(node.id, { width: 250, height: 100 });
        });

        // Add all edges including the new one
        const allEdges = [...edges, newEdge];
        allEdges.forEach(edge => {
          dagreGraph.setEdge(edge.source, edge.target);
        });

        // Layout the graph
        dagre.layout(dagreGraph);

        // Get positioned nodes
        const layoutedNodes = allNodes.map(node => {
          const nodeWithPosition = dagreGraph.node(node.id);
          return {
            ...node,
            position: {
              x: nodeWithPosition.x - 125,
              y: nodeWithPosition.y - 50
            }
          };
        });

        // Update edges in next tick
        setTimeout(() => {
          setEdges(eds => [...eds, newEdge]);
        }, 0);

        // Set label selector position based on the new node's position
        const newNodePosition = layoutedNodes.find(
          n => n.id === newNodeId
        )?.position;
        if (newNodePosition) {
          setTimeout(() => {
            setActiveLabelSelector({
              nodeId: newNodeId,
              position: {
                x: newNodePosition.x + 270,
                y: newNodePosition.y + 50
              }
            });
          }, 50);
        }

        return layoutedNodes;
      });
    },
    [edges, getAvailableLabelsCount]
  );

  const handleLabelSelect = useCallback(
    (labelData: Label) => {
      if (!activeLabelSelector) return;

      setNodes(nds =>
        nds.map(node => ({
          ...node,
          data: {
            ...node.data,
            ...(node.id === activeLabelSelector.nodeId
              ? {
                  label: labelData.label_name,
                  labelId: labelData.id
                }
              : {}),
            isLabelSelectionActive: false // Reset the flag for all nodes
          }
        }))
      );

      setActiveLabelSelector(null);
    },
    [activeLabelSelector]
  );

  const LabelSelector: React.FC = () => {
    if (!activeLabelSelector || !getExistingLabels?.data?.labels) return null;

    // Filter out only currently used labels
    const availableLabels = getExistingLabels.data.labels.filter(
      (label: Label) => !usedLabels.has(label.label_name)
    );

    return (
      <div className={Styles.labelSelector}>
        <h3>Select a Label</h3>
        <div
          className={clsx(
            Styles.labelList,
            'scrollbar scrollbar-track-gray-100 scrollbar-thumb-[#102433] hover:scrollbar-thumb-[#1c3a4f] scrollbar-thumb-rounded-full scrollbar-track-rounded-full overflow-auto'
          )}
        >
          {availableLabels.map((label: Label) => (
            <button
              key={label.id}
              onClick={() => handleLabelSelect(label)}
              className={Styles.labelButton}
            >
              {label.label_name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const EditLabelDialog: React.FC<EditLabelDialogProps> = ({
    nodeId,
    show
  }) => {
    if (!show || !getExistingLabels?.data?.labels) return null;

    const currentNode = nodes.find(n => n.id === nodeId);
    const availableLabels = getExistingLabels.data.labels.filter(
      (label: Label) =>
        !usedLabels.has(label.label_name) ||
        currentNode?.data.label === label.label_name
    );

    return (
      <div className={Styles.editLabelDialog}>
        <h3>Edit Label</h3>
        <div className={Styles.labelList}>
          {availableLabels.map((label: Label) => (
            <button
              key={label.id}
              onClick={() => {
                setNodes(nds =>
                  nds.map(node =>
                    node.id === nodeId
                      ? {
                          ...node,
                          data: {
                            ...node.data,
                            label: label.label_name,
                            labelId: label.id
                          }
                        }
                      : node
                  )
                );

                // No need to manually update usedLabels as it will be handled by the useEffect
                setEditingNodeId(null);
              }}
              className={Styles.labelButton}
            >
              {label.label_name}
            </button>
          ))}
        </div>
        <button
          onClick={() => setEditingNodeId(null)}
          className={Styles.cancelButton}
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <div className="w-full p-4 flex flex-col gap-3">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Activity Flow</h2>
        <SearchSelect
          data={[
            ...(configs?.data.map((config: any) => ({
              label: config.name,
              value: config.id
            })) || [])
          ]}
          disabled
          width="12vw"
          label="Select Configuration"
          selectValue={currentConfigId}
          placeholder="Configuration"
        />
      </div>
      <div
        className={Styles.canvasContainer}
        style={{ height: '65vh' }}
        ref={reactFlowWrapper}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={{ custom: CustomNode }}
          fitView
          defaultEdgeOptions={edgeOptions}
          connectionMode={ConnectionMode.Strict}
          snapToGrid={true}
          snapGrid={[20, 20]}
          minZoom={0.1}
          maxZoom={1.5}
          nodesDraggable={true}
          elementsSelectable={true}
          deleteKeyCode={null}
          panOnDrag={true}
          onInit={setReactFlowInstance}
        >
          <Controls className="!left-auto !right-4" />
          <MiniMap />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
        <LabelSelector />
        <EditLabelDialog nodeId={editingNodeId} show={editingNodeId !== null} />
      </div>
      <div className={Styles.buttonholder}>
        <Button
          variant="contained"
          style={{
            background: 'transparent',
            color: '#102633',
            border: '1px solid #102633'
          }}
          onClick={() => navigate('/train/pick_&_place_annotation')}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            let temp = [...objectDependency];
            dispatch(setObjectMapping(temp));
            navigate(`/train/workflowconfig/pick&place`);
          }}
          disabled={!canSaveAndProceed}
        >
          Save & Continue
        </Button>
      </div>
    </div>
  );
};

export default ActivityFlow;
